import { useEffect, useState } from "react";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, RefreshCw } from "lucide-react";
import { devError } from "@/lib/logger";

const EMPTY = {
  code: "", name: "", designation: "", department: "", joining_date: new Date().toISOString().slice(0, 10),
  status: "Active",
  pan: "", aadhaar_last4: "", pf_uan: "", esi_no: "",
  pf_enabled: true, esi_enabled: true,
  bank_account_no: "", bank_ifsc: "", bank_name: "",
  basic: 0, hra: 0, allowance: 0, bonus: 0, other_earnings: 0,
  monthly_tds: 0, other_deductions: 0,
  address: "", contact: "",
};

function EmployeeForm({ open, onClose, initial, onSaved }) {
  const { activeId } = useCompany();
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNum = (k, v) => set(k, v === "" ? 0 : Number(v));

  const gross = Number(form.basic || 0) + Number(form.hra || 0) + Number(form.allowance || 0) + Number(form.bonus || 0) + Number(form.other_earnings || 0);

  const onSave = async () => {
    if (!form.code || !form.name) {
      toast.error("Code and Name are required");
      return;
    }
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/employees/${initial.id}`, form);
        toast.success(`Updated ${form.name}`);
      } else {
        await api.post(`/companies/${activeId}/employees`, form);
        toast.success(`Added ${form.name}`);
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="employee-form">
        <DialogHeader>
          <DialogTitle>{initial?.id ? `Edit ${initial.name}` : "Add Employee"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Identity */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div><Label className="text-[10px] uppercase tracking-wider text-gray-600">Code *</Label><Input value={form.code} onChange={(e) => set("code", e.target.value)} className="h-8" data-testid="emp-code" /></div>
            <div className="md:col-span-2"><Label className="text-[10px] uppercase tracking-wider text-gray-600">Name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="h-8" data-testid="emp-name" /></div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-gray-600">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="h-8" data-testid="emp-status"><SelectValue /></SelectTrigger>
                <SelectContent>{["Active", "Resigned", "Terminated"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div><Label className="text-[10px] uppercase tracking-wider text-gray-600">Designation</Label><Input value={form.designation} onChange={(e) => set("designation", e.target.value)} className="h-8" data-testid="emp-designation" /></div>
            <div><Label className="text-[10px] uppercase tracking-wider text-gray-600">Department</Label><Input value={form.department} onChange={(e) => set("department", e.target.value)} className="h-8" data-testid="emp-department" /></div>
            <div><Label className="text-[10px] uppercase tracking-wider text-gray-600">Joining Date</Label><Input type="date" value={form.joining_date} onChange={(e) => set("joining_date", e.target.value)} className="h-8" data-testid="emp-joining-date" /></div>
            <div><Label className="text-[10px] uppercase tracking-wider text-gray-600">Contact</Label><Input value={form.contact} onChange={(e) => set("contact", e.target.value)} className="h-8" data-testid="emp-contact" /></div>
          </div>

          {/* Statutory */}
          <div className="border-t pt-3">
            <div className="text-xs font-semibold text-gray-700 mb-2">Statutory IDs</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div><Label className="text-[10px] uppercase tracking-wider text-gray-600">PAN</Label><Input value={form.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} className="h-8 font-mono" maxLength={10} data-testid="emp-pan" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-gray-600">Aadhaar (last 4)</Label><Input value={form.aadhaar_last4} onChange={(e) => set("aadhaar_last4", e.target.value)} className="h-8 font-mono" maxLength={4} data-testid="emp-aadhaar" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-gray-600">PF UAN</Label><Input value={form.pf_uan} onChange={(e) => set("pf_uan", e.target.value)} className="h-8 font-mono" data-testid="emp-pf-uan" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-gray-600">ESI Number</Label><Input value={form.esi_no} onChange={(e) => set("esi_no", e.target.value)} className="h-8 font-mono" data-testid="emp-esi-no" /></div>
            </div>
            <div className="flex items-center gap-6 mt-3">
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input type="checkbox" checked={form.pf_enabled} onChange={(e) => set("pf_enabled", e.target.checked)} data-testid="emp-pf-enabled" />
                PF applicable
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input type="checkbox" checked={form.esi_enabled} onChange={(e) => set("esi_enabled", e.target.checked)} data-testid="emp-esi-enabled" />
                ESI applicable
              </label>
            </div>
          </div>

          {/* Bank */}
          <div className="border-t pt-3">
            <div className="text-xs font-semibold text-gray-700 mb-2">Bank Details</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label className="text-[10px] uppercase tracking-wider text-gray-600">Bank Name</Label><Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} className="h-8" data-testid="emp-bank-name" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-gray-600">Account No.</Label><Input value={form.bank_account_no} onChange={(e) => set("bank_account_no", e.target.value)} className="h-8 font-mono" data-testid="emp-bank-acno" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-gray-600">IFSC</Label><Input value={form.bank_ifsc} onChange={(e) => set("bank_ifsc", e.target.value.toUpperCase())} className="h-8 font-mono" maxLength={11} data-testid="emp-bank-ifsc" /></div>
            </div>
          </div>

          {/* Pay structure */}
          <div className="border-t pt-3">
            <div className="text-xs font-semibold text-gray-700 mb-2">Monthly Pay Structure (₹)</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                ["Basic", "basic"], ["HRA", "hra"], ["Allowance", "allowance"], ["Bonus", "bonus"], ["Other Earnings", "other_earnings"],
              ].map(([label, key]) => (
                <div key={key}><Label className="text-[10px] uppercase tracking-wider text-gray-600">{label}</Label><Input type="number" min="0" value={form[key]} onChange={(e) => setNum(key, e.target.value)} className="h-8 font-mono tabular-nums" data-testid={`emp-${key.replace("_", "-")}`} /></div>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
              <div><Label className="text-[10px] uppercase tracking-wider text-gray-600">Monthly TDS</Label><Input type="number" min="0" value={form.monthly_tds} onChange={(e) => setNum("monthly_tds", e.target.value)} className="h-8 font-mono tabular-nums" data-testid="emp-monthly-tds" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-gray-600">Other Deductions</Label><Input type="number" min="0" value={form.other_deductions} onChange={(e) => setNum("other_deductions", e.target.value)} className="h-8 font-mono tabular-nums" data-testid="emp-other-deductions" /></div>
              <div className="self-end">
                <div className="text-[10px] uppercase tracking-wider text-gray-500">Computed Monthly Gross</div>
                <div className="font-mono text-lg font-semibold text-blue-700" data-testid="emp-gross">₹ {formatINR(gross)}</div>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="emp-cancel">Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="emp-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Inner() {
  const { activeId } = useCompany();
  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!activeId) return;
    let mounted = true;
    api.get(`/companies/${activeId}/employees`, { params: statusFilter !== "All" ? { status: statusFilter } : {} })
      .then((r) => { if (mounted) setList(r.data); })
      .catch((err) => devError("List employees failed", err));
    return () => { mounted = false; };
  }, [activeId, statusFilter, reloadTick]);

  const refetch = () => setReloadTick((t) => t + 1);

  const onDelete = async (emp) => {
    if (!window.confirm(`Delete ${emp.name}?`)) return;
    try {
      await api.delete(`/employees/${emp.id}`);
      toast.success(`Deleted ${emp.name}`);
      refetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <>
      <PageHeader
        title="Employees"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={refetch} className="h-8" data-testid="btn-refresh-employees"><RefreshCw className="w-4 h-4" /></Button>
            <Button onClick={() => { setEditing(null); setOpen(true); }} className="h-8" data-testid="btn-add-employee"><Plus className="w-4 h-4 mr-1" /> Add Employee</Button>
          </div>
        }
      />
      <div className="p-6 space-y-4" data-testid="employees-page">
        <div className="flex items-center gap-3">
          <Label className="text-[10px] uppercase tracking-wider text-gray-500">Filter status:</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-40" data-testid="select-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["All", "Active", "Resigned", "Terminated"].map(s => <SelectItem key={s} value={s} data-testid={`opt-status-${s}`}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-xs text-gray-500 ml-auto">{list.length} employee{list.length !== 1 ? "s" : ""}</div>
        </div>

        <div className="border border-gray-200 rounded bg-white overflow-hidden">
          <table className="tx-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Designation</th>
                <th>Department</th>
                <th>PF UAN</th>
                <th className="text-right">Basic</th>
                <th className="text-right">Gross</th>
                <th>Status</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody data-testid="employees-rows">
              {list.length === 0 ? (
                <tr><td colSpan={9} className="text-center text-xs text-gray-400 py-6">No employees yet. Click &quot;Add Employee&quot; to get started.</td></tr>
              ) : list.map((e) => {
                const gross = Number(e.basic) + Number(e.hra) + Number(e.allowance) + Number(e.bonus) + Number(e.other_earnings || 0);
                return (
                  <tr key={e.id} data-testid={`emp-row-${e.code}`}>
                    <td className="font-mono text-xs">{e.code}</td>
                    <td className="text-xs font-semibold">{e.name}</td>
                    <td className="text-xs">{e.designation || "—"}</td>
                    <td className="text-xs">{e.department || "—"}</td>
                    <td className="font-mono text-xs">{e.pf_uan || "—"}</td>
                    <td className="tx-num">{formatINR(e.basic)}</td>
                    <td className="tx-num font-semibold">{formatINR(gross)}</td>
                    <td><span className={`text-[11px] border rounded px-2 py-0.5 ${e.status === "Active" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>{e.status}</span></td>
                    <td className="text-right">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setEditing(e); setOpen(true); }} data-testid={`btn-edit-${e.code}`}><Pencil className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-600 hover:bg-red-50" onClick={() => onDelete(e)} data-testid={`btn-delete-${e.code}`}><Trash2 className="w-3 h-3" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <EmployeeForm key={editing?.id || `new-${open ? "1" : "0"}`} open={open} onClose={() => setOpen(false)} initial={editing} onSaved={refetch} />
    </>
  );
}

export default function Employees() {
  return <RequireCompany><Inner /></RequireCompany>;
}
